/* eslint-disable react-refresh/only-export-components */
import { createContext, Dispatch, FC, MouseEvent, Reducer, useReducer } from 'react';

import { BannerSecondaryElementProps } from 'app/components/v2/components/Notification/BannerSecondaryElement';
import { PropsWithOnlyChildren } from 'app/types';
import { removeItemFromArray } from 'app/utils/lib/array';

// Local Typings
interface AlertActionButton {
  isPrimaryAction?: boolean;
  label: string;
  onClick: (e: MouseEvent) => void;
}
export type AlertLevel = 'error' | 'info' | 'success' | 'warning';
type AlertType = 'alert' | 'notification';
export interface Alert {
  actions: AlertActionButton[];
  alertLevel: AlertLevel;
  alertType: AlertType;
  index?: number;
  itemId?: null | string;
  message?: string;
  onClickClose?: () => void;
  onTimeoutClose?: () => void;
  secondaryContent?: BannerSecondaryElementProps;
  shouldAutoHide?: boolean;
  timestamp: number;
  title: string;
}
interface State {
  alerts: Alert[];
}

interface ResetAction {
  type: 'reset';
}
interface AddAlertAction {
  payload: {
    alert: Partial<Omit<Alert, 'title'>> & Pick<Alert, 'title'>;
  };
  type: 'addAlert';
}
interface RemoveAlertAction {
  payload: {
    removeTimestamp: number;
  };
  type: 'removeAlert';
}
export type AlertsContextAction =
  | AddAlertAction
  | RemoveAlertAction
  | ResetAction;
export type AlertsDispatch = Dispatch<AlertsContextAction>;

// Local Variables
const initialState: State = {
  alerts: [],
};

export const AlertsStateContext = createContext(initialState);
export const AlertsDispatchContext = createContext<AlertsDispatch>(undefined as unknown as AlertsDispatch);

AlertsStateContext.displayName = 'AlertsState';
AlertsDispatchContext.displayName = 'AlertsDispatch';

const reducer: Reducer<State, AlertsContextAction> = (state, action) => {
  switch (action.type) {
    case 'addAlert': {
      const defaultAlert: Omit<Alert, 'title'> = {
        actions: [],
        alertLevel: 'info',
        alertType: 'notification',
        timestamp: Date.now(),
      };

      return {
        ...state,
        alerts: [
          ...state.alerts,
          {
            ...defaultAlert,
            ...action.payload.alert,
          },
        ],
      };
    }
    case 'reset':
      return { ...initialState };
    case 'removeAlert': {
      const removeIndex = state.alerts.findIndex(alert => alert.timestamp === action.payload.removeTimestamp);

      return {
        ...state,
        alerts: removeItemFromArray(state.alerts,
          removeIndex),
      };
    }
    default:
      throw new Error();
  }
};

const AlertsContextProvider: FC<PropsWithOnlyChildren> = ({ children }) => {
  const [state, dispatch] = useReducer(reducer, initialState);

  return (
    <AlertsStateContext.Provider value={state}>
      <AlertsDispatchContext.Provider value={dispatch}>
        {children}
      </AlertsDispatchContext.Provider>
    </AlertsStateContext.Provider>
  );
};

export default AlertsContextProvider;
